import { ArrowDown } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { type CSSProperties, type ReactNode, useState } from "react";

import { Line } from "../Line";
import { Pressable } from "../Pressable";
import { Tag } from "../Tag";
import { TenantAvatar } from "../TenantAvatar";
import { Label } from "../typography/Label";
import { chatInputMotionTransition } from "./input/motion";

export function ChatMessagePill({ text }: { text: string }) {
    return (
        <div className="pointer-events-none sticky top-0 z-[400] flex w-full items-center justify-center py-16">
            <Tag variant="tertiary">{text}</Tag>
        </div>
    );
}

type NewMessagesButtonProps = {
    children: ReactNode;
    className?: string;
    onPress?: () => void;
    style?: CSSProperties;
};

export function NewMessagesButton({
    children,
    className,
    onPress,
    style,
}: NewMessagesButtonProps) {
    return (
        <Pressable className={className} style={style} onPress={onPress}>
            <Tag variant="tertiary" className="items-center gap-4">
                {children}
                <ArrowDown aria-hidden="true" className="size-16" />
            </Tag>
        </Pressable>
    );
}

export function NewMessagesLabel({
    children = "New messages",
}: {
    children?: ReactNode;
}) {
    return (
        <div className="my-24 flex flex-row items-center gap-16 md:my-16 md:px-40">
            <Line className="inline-flex w-full" />
            <span className="inline-flex flex-nowrap">
                <Label
                    size="small"
                    weight="regular"
                    color="tertiary"
                    transform="uppercase"
                    className="whitespace-nowrap"
                >
                    {children}
                </Label>
            </span>
            <Line className="inline-flex w-full" />
        </div>
    );
}

const requestStatusBadgeVariants = {
    hidden: {
        height: 0,
        opacity: 0,
    },
    visible: {
        height: "auto",
        opacity: 1,
    },
};

type RequestStatusBadgeProps = {
    animate?: boolean;
    description?: ReactNode;
    isVisible?: boolean;
    title?: ReactNode;
};

export function RequestStatusBadge({
    animate = false,
    description = "Your concierge will respond shortly",
    isVisible = true,
    title = "Your message has been delivered",
}: RequestStatusBadgeProps) {
    const content = (
        <div className="flex w-full flex-col items-center gap-12 pt-40 pb-16">
            <TenantAvatar size="large" />

            <Label size="medium" weight="medium-bold">
                {title}
            </Label>

            <Label size="small" weight="regular" color="tertiary">
                {description}
            </Label>
        </div>
    );

    if (!animate) {
        return isVisible ? content : null;
    }

    return (
        <AnimatePresence initial={false}>
            {isVisible ? (
                <motion.div
                    key="request-status-badge"
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={requestStatusBadgeVariants}
                    transition={chatInputMotionTransition}
                    className="w-full overflow-hidden"
                >
                    {content}
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

export function RequestStatusDisclosure() {
    const [isVisible, setIsVisible] = useState(true);

    return (
        <div className="flex w-full flex-col items-start gap-12">
            <Pressable
                aria-expanded={isVisible}
                onPress={() => {
                    setIsVisible((current) => !current);
                }}
            >
                {isVisible ? "Hide status badge" : "Show status badge"}
            </Pressable>

            <RequestStatusBadge animate isVisible={isVisible} />
        </div>
    );
}
